import turf from 'src/utils/turf'
import { atom, selector } from 'recoil'
import {
  AMMobilityPolygonAllRankResponse,
  AMMobilityPolygonTopLocation,
  MultiPolygon,
  Polygon,
  Position,
} from 'src/common-services'
import { geoJSON, hasCustomArea } from 'src/atoms/custom-area'
import { alphamapClient, partnersClient, venueClient } from 'src/clients'
import { DEFAULT_LRU_SIZE, keyFactory, uncachedSelector, uncachedSelectorFamily } from 'src/utils/recoil'
import { googlePlace } from 'src/atoms/search'
import { Bounds } from 'src/types/map'
import { toLatLng, toPosition } from 'src/utils/map'
import { getDateWithoutBloodyTZ } from 'src/utils/date-utils'
import * as mobilityAtoms from 'src/atoms/mobility'
import * as matthewsAtoms from 'src/atoms/matthews'

const key = keyFactory('footfall-area')

export const MIN_RADIUS = 0.25 // Miles
export const DEF_RADIUS = 1 // Miles
export const MAX_RADIUS = 20 // Miles
export const MIN_VENUES_IN_AREA = 10
export const MAX_VENUES = 100 // Should be enough venues without lagging the map
export const CIRCLE_CORRECTION = 0.1

const DEFAULT_LOCATION = [-97.7256041, 30.34303520000001]

export const forceArea = atom<boolean>({
  key: key('force'),
  default: false,
})

export const areaRadiusIsDragging = atom<boolean>({
  key: key('area-radius-is-dragging'),
  default: false,
})

export const areaRadius = atom<number | undefined>({
  key: key('area-radius'),
  default: undefined,
})

export const locationsPage = atom<number>({
  key: key('locations-page'),
  default: 1,
})

const nearestVenuesInArea = uncachedSelector({
  key: key('nearest-venues-in-area'),
  get: ({ get }) => {
    const place = get(googlePlace)
    if (!place) {
      return []
    }
    const radiusValue = get(areaRadius)

    return venueClient.getNearestVenues({
      point: {
        type: 'Point',
        coordinates: place.geometry.location,
      },
      radiusMiles: radiusValue ? radiusValue : DEF_RADIUS,
      maxNumber: MIN_VENUES_IN_AREA,
    })
  },
  lru: DEFAULT_LRU_SIZE,
})

const nearestVenuesRadius = uncachedSelector({
  key: key('nearest-venues-radius'),
  get: ({ get }) => {
    const place = get(googlePlace)
    if (!place) {
      return 0
    }
    const venues = get(nearestVenuesInArea)
    const distances = venues.map((venue) =>
      turf.distance(place.geometry.location, venue.point.coordinates, { units: 'miles' })
    )
    if (distances.length) {
      return Math.max(...distances)
    }
    return 0
  },
  lru: DEFAULT_LRU_SIZE,
})

export const useArea = selector<boolean>({
  key: key('use'),
  get: ({ get }) => {
    const hasCustomAreaValue = get(hasCustomArea)
    const forceAreaValue = get(forceArea)
    return hasCustomAreaValue || forceAreaValue
  },
})

export const derivedRadius = selector<number>({
  key: key('derived-radius'),
  get: ({ get }) => {
    const hasCustomAreaValue = get(hasCustomArea)
    if (hasCustomAreaValue) {
      return 0
    }
    const nearestVenuesRadiusValue = get(nearestVenuesRadius)
    return parseFloat(Math.max(nearestVenuesRadiusValue + CIRCLE_CORRECTION, MIN_RADIUS).toPrecision(2))
  },
})

export const polygon = uncachedSelector({
  key: key('polygon'),
  get: ({ get }) => {
    const hasCustomAreaValue = get(hasCustomArea)
    const place = get(googlePlace)

    if (hasCustomAreaValue) {
      return get(geoJSON)
    }
    if (place) {
      const derivedRadiusValue = get(derivedRadius)
      const radiusValue = get(areaRadius)

      return turf.circle(place.geometry.location, radiusValue || derivedRadiusValue, { units: 'miles' }).geometry
    }
    return turf.circle(DEFAULT_LOCATION, MIN_RADIUS, { units: 'miles' }).geometry
  },
  lru: DEFAULT_LRU_SIZE,
})

export const polygonByPosition = uncachedSelectorFamily<Polygon, Position>({
  key: key('polygon-by-position'),
  get:
    (position) =>
    ({ get }) => {
      const hasCustomAreaValue = get(hasCustomArea)
      const place = get(googlePlace)

      if (hasCustomAreaValue) {
        return get(geoJSON)
      }
      if (place) {
        const derivedRadiusValue = get(derivedRadius)
        const radiusValue = get(areaRadius)

        return turf.circle(position, radiusValue || derivedRadiusValue, { units: 'miles' }).geometry
      }
      return turf.circle(DEFAULT_LOCATION, MIN_RADIUS, { units: 'miles' }).geometry
    },
  lru: DEFAULT_LRU_SIZE,
})

export const multipolygon = uncachedSelector({
  key: key('multipolygon'),
  get: ({ get }) => {
    const poly = get(polygon)
    const multi: MultiPolygon = {
      type: 'MultiPolygon',
      coordinates: [poly.coordinates],
    }
    return multi
  },
  lru: DEFAULT_LRU_SIZE,
})

export const centroid = uncachedSelector<Position | undefined>({
  key: key('centroid'),
  get: ({ get }) => {
    const poly = get(polygon)
    if (poly) {
      return turf.centroid(poly).geometry.coordinates
    }
    return undefined
  },
  lru: DEFAULT_LRU_SIZE,
})

export const bounds = uncachedSelector<Bounds>({
  key: key('bounds'),
  get: ({ get }) => {
    const geo = get(polygon)
    const center = get(centroid)
    const googleBounds = new google.maps.LatLngBounds()
    for (const point of geo.coordinates[0]) {
      googleBounds.extend(toLatLng(point))
    }
    return {
      northEast: toPosition(googleBounds.getNorthEast()),
      center: center ?? [0, 0],
      southWest: toPosition(googleBounds.getSouthWest()),
    }
  },
  lru: DEFAULT_LRU_SIZE,
})

export const venuesInArea = uncachedSelector({
  key: key('venues-in-area'),
  get: ({ get }) => {
    const shape = get(multipolygon)
    return venueClient.getNearestVenues({ shape, maxNumber: MAX_VENUES })
  },
  lru: DEFAULT_LRU_SIZE,
})

export const locationsInArea = uncachedSelector<
  AMMobilityPolygonTopLocation[] | { locations: AMMobilityPolygonTopLocation[]; totalLocations: number }
>({
  key: key('locations-in-area'),
  get: ({ get }) => {
    const iframed = get(matthewsAtoms.isIframe)
    const shape = get(multipolygon)
    if (iframed) {
      return get(locationsInPolygon({ coordinates: shape.coordinates[0] })) as {
        locations: AMMobilityPolygonTopLocation[]
        totalLocations: number
      }
    }
    return get(locationsInPolygon({ coordinates: shape.coordinates[0] })) as AMMobilityPolygonTopLocation[]
  },
  lru: DEFAULT_LRU_SIZE,
})

export const locationsInPolygon = uncachedSelectorFamily<
  AMMobilityPolygonTopLocation[] | { locations: AMMobilityPolygonTopLocation[]; totalLocations: number },
  { coordinates: Position[][] }
>({
  key: key('locations-in-polygon'),
  get:
    ({ coordinates }) =>
    async ({ get }) => {
      const iframed = get(matthewsAtoms.isIframe)
      const polygon: Polygon = {
        type: 'Polygon',
        coordinates: coordinates,
      }
      if (iframed) {
        const page = get(locationsPage)
        return await partnersClient.getMatthewsLocationsInArea({ polygon, page, limit: 100 })
      }

      return alphamapClient.mobility.getPolygonTopLocations({
        polygon,
        limit: 1000,
        page: 1,
      })
    },
  lru: DEFAULT_LRU_SIZE,
})

export const locationsInAreaList = uncachedSelector<AMMobilityPolygonTopLocation[]>({
  key: key('locations-in-area-list'),
  get: ({ get }) => {
    const page = get(locationsPage)
    const shape = get(multipolygon)
    return get(locationsInAreaByCoordinates({ coordinates: shape.coordinates[0], page, rowsPerPage: 100 }))
  },
  lru: DEFAULT_LRU_SIZE,
})

export const locationsInAreaByCoordinates = uncachedSelectorFamily<
  AMMobilityPolygonTopLocation[],
  { coordinates: Position[][]; page?: number; rowsPerPage?: number }
>({
  key: key('locations-in-area-by-coordinates'),
  get:
    ({ coordinates, page = 1, rowsPerPage = 1000 }) =>
    ({ get }) => {
      const iframed = get(matthewsAtoms.isIframe)
      const polygon: Polygon = {
        type: 'Polygon',
        coordinates: coordinates,
      }

      if (iframed) {
        return partnersClient.getMatthewsTopLocationsInArea({ polygon, limit: rowsPerPage, page })
      }

      return alphamapClient.mobility.getPolygonTopLocations({ polygon, limit: rowsPerPage, page })
    },
  lru: DEFAULT_LRU_SIZE,
})

export const allRanksInArea = uncachedSelector({
  key: key('all-ranks-in-area'),
  get: ({ get }) => {
    const page = get(locationsPage)
    const iframed = get(matthewsAtoms.isIframe)
    const shape = get(multipolygon)
    const polygon: Polygon = {
      type: 'Polygon',
      coordinates: shape.coordinates[0],
    }

    if (iframed) {
      return partnersClient.getMatthewsLocationRanks({ polygon, limit: 100, page })
    }

    return alphamapClient.mobility.getPolygonAllRank({ polygon, limit: 100, page })
  },
  lru: DEFAULT_LRU_SIZE,
})

export const allRanksInAreaMap = selector<Map<string, AMMobilityPolygonAllRankResponse['rank']>>({
  key: key('all-ranks-in-area-map'),
  get: ({ get }) => {
    const allRanks = get(allRanksInArea)
    const map = new Map<string, AMMobilityPolygonAllRankResponse['rank']>()
    for (const location of allRanks) {
      map.set(location.id, location.rank)
    }
    return map
  },
})

export const showRankedLocationsOnly = atom<boolean>({
  key: key('show-ranked-locations-only'),
  default: false,
})

// User can hide specific locations in the nearby locations table
export const hiddenLocationsIds = atom<string[]>({
  key: key('hidden-locations-ids'),
  default: [],
})

export const hiddenLocations = selector<AMMobilityPolygonTopLocation[]>({
  key: key('hidden-locations'),
  get: ({ get }) => {
    const blacklistedIds = get(hiddenLocationsIds)
    const areaLocations = get(locationsInArea)
    const locations = 'totalLocations' in areaLocations ? areaLocations.locations : areaLocations
    return locations.filter((location) => blacklistedIds.includes(location.id))
  },
})

// Valid end dates
export const areaVisitsValidEndDate = selector<string>({
  key: key('valid-end-date'),
  get: async ({ get }) => {
    const locationId = get(mobilityAtoms.alphamapLocationId)
    if (!locationId) {
      return get(mobilityAtoms.validEndDateString)
    }
    const endDate = get(mobilityAtoms.validAdvanEndDateForLocations([locationId]))
    return endDate
  },
})

export const areaVisitsMaxEndDate = selector<Date>({
  key: key('max-end-date'),
  get: ({ get }) => {
    const date = get(areaVisitsValidEndDate)
    return getDateWithoutBloodyTZ(date)
  },
})
